<!--
  One house (/a/:id): a shared link, so it's often someone's first look at corpers.ng. On a phone, one column with
  the actions in a bar at the bottom; from lg, the photos and details on the left and a sticky panel on the right
  (rent, the actions, the numbers, the poster). The window scrolls (this route is outside NavComponent).
-->
<div class="min-h-dvh bg-background-light">
  <app-listing-page-header backLink="/houses" backLabel="All houses"></app-listing-page-header>

  <main class="mx-auto max-w-5xl px-4 pt-2 pb-32 lg:pb-12">
    <!-- Loading: shaped like the page, so it doesn't jump when the house arrives -->
    <div *ngIf="isLoading" class="animate-pulse space-y-4" aria-busy="true" aria-label="Loading the house">
      <div class="aspect-[4/3] rounded-xl bg-gray-200 sm:aspect-[16/10]"></div>
      <div class="space-y-3 rounded-xl bg-white p-5 shadow-card">
        <div class="h-7 w-1/2 rounded bg-gray-200"></div>
        <div class="h-4 w-1/3 rounded bg-gray-200"></div>
        <div class="h-6 w-1/4 rounded bg-gray-200"></div>
      </div>
    </div>

    <div *ngIf="!isLoading && notFound" class="flex flex-col items-center gap-3 py-20 text-center">
      <mat-icon class="!h-12 !w-12 !text-5xl text-primary/30" aria-hidden="true">apartment</mat-icon>
      <p class="m-0 text-base font-medium text-gray-900">This house isn't listed any more.</p>
      <p class="m-0 text-sm text-text-secondary">It may have been taken, or removed by the poster.</p>
      <a routerLink="/houses" class="btn-primary mt-2 inline-flex items-center">See other houses</a>
    </div>

    <div *ngIf="!isLoading && loadError" class="flex flex-col items-center gap-3 py-20 text-center">
      <mat-icon class="!h-12 !w-12 !text-5xl text-primary/30" aria-hidden="true">wifi_off</mat-icon>
      <p class="m-0 text-base font-medium text-gray-900">Couldn't load this house.</p>
      <p class="m-0 text-sm text-text-secondary">Check your connection and try again.</p>
      <button type="button" class="btn-primary mt-2 inline-flex items-center gap-2" (click)="loadAccommodation()">
        <mat-icon aria-hidden="true">refresh</mat-icon> Try again
      </button>
    </div>

    <div *ngIf="!isLoading && accommodation" class="lg:grid lg:grid-cols-[minmax(0,1fr)_22rem] lg:items-start lg:gap-6">
      <div class="min-w-0 space-y-4">
        <!-- Photos -->
        <div class="relative aspect-[4/3] overflow-hidden rounded-xl bg-primary/5 shadow-card sm:aspect-[16/10]">
          <app-photo-strip
      placeholderIcon="apartment"
            [media]="accommodation.Media"
            [alt]="typeLabel + (location ? ' in ' + location : '')"
            arrows="always"
            [width]="1000"
            sizes="(min-width: 1024px) 40rem, 100vw"
          ></app-photo-strip>
          <div *ngIf="isFree || seekingRoommate || accommodation.is_admin_post" class="pointer-events-none absolute left-3 top-3 flex flex-wrap gap-1.5">
            <span *ngIf="isFree" class="rounded-lg bg-accent px-2 py-1 text-sm font-medium text-primary shadow-sm">Free lodge</span>
            <span *ngIf="seekingRoommate" class="rounded-lg bg-white/90 px-2 py-1 text-sm font-medium text-primary shadow-sm">Seeking a roommate</span>
            <span *ngIf="accommodation.is_admin_post" class="rounded-lg bg-white/90 px-2 py-1 text-sm font-medium text-primary shadow-sm">Admin</span>
          </div>
        </div>

        <div class="lg:hidden">
          <ng-container *ngTemplateOutlet="summary; context: { $implicit: false }"></ng-container>
        </div>

        <section *ngIf="facts.length" class="rounded-xl bg-white p-5 shadow-card">
          <h2 class="mb-4 text-lg font-semibold text-primary">About the place</h2>
          <dl class="m-0 grid gap-4 sm:grid-cols-2">
            <div *ngFor="let fact of facts" class="flex items-start gap-3">
              <span class="flex size-10 flex-shrink-0 items-center justify-center rounded-lg bg-background-light text-primary" aria-hidden="true">
                <mat-icon class="!size-5 !text-xl !leading-5">{{ fact.icon }}</mat-icon>
              </span>
              <div class="min-w-0">
                <dt class="text-sm text-text-secondary">{{ fact.label }}</dt>
                <dd class="m-0 font-medium text-gray-900">
                  <time *ngIf="fact.date; else plainValue" [attr.datetime]="fact.value">{{ fact.value | date: 'd MMMM y' }}</time>
                  <ng-template #plainValue>{{ fact.value }}</ng-template>
                </dd>
              </div>
            </div>
          </dl>
        </section>

        <section *ngIf="accommodation.occupant_description" class="rounded-xl bg-white p-5 shadow-card">
          <h2 class="mb-2 text-lg font-semibold text-primary">Who lives there now</h2>
          <p class="m-0 whitespace-pre-line leading-relaxed text-gray-800">{{ accommodation.occupant_description }}</p>
        </section>

        <section *ngIf="accommodation.ideal_roommate" class="rounded-xl bg-white p-5 shadow-card">
          <h2 class="mb-2 text-lg font-semibold text-primary">The roommate they're looking for</h2>
          <p class="m-0 whitespace-pre-line leading-relaxed text-gray-800">{{ accommodation.ideal_roommate }}</p>
        </section>

        <div class="space-y-4 lg:hidden">
          <ng-container *ngTemplateOutlet="contact"></ng-container>
          <ng-container *ngTemplateOutlet="poster"></ng-container>
        </div>

        <!-- The about page's safety guidelines, where they matter most -->
        <p class="m-0 flex gap-3 rounded-xl border border-primary/10 bg-white/60 p-4 text-sm leading-relaxed text-text-secondary">
          <mat-icon class="flex-shrink-0 text-primary/70" aria-hidden="true">verified_user</mat-icon>
          <span>Inspect the house and meet the landlord before you pay anything - and pay the landlord directly.
            <a routerLink="/about" fragment="disclaimer" class="font-medium text-primary underline">Staying safe</a></span>
        </p>
      </div>

      <aside class="hidden space-y-4 lg:sticky lg:top-24 lg:block">
        <ng-container *ngTemplateOutlet="summary; context: { $implicit: true }"></ng-container>
        <ng-container *ngTemplateOutlet="contact"></ng-container>
        <ng-container *ngTemplateOutlet="poster"></ng-container>
      </aside>
    </div>
  </main>

  <!-- On a phone, the actions stay in reach at the bottom -->
  <div *ngIf="!isLoading && accommodation"
    class="fixed inset-x-0 bottom-0 z-20 flex items-center gap-2 border-t border-gray-200 bg-white px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] lg:hidden">
    <ng-container *ngTemplateOutlet="actions; context: { $implicit: 'bar' }"></ng-container>
  </div>
</div>

<!-- What, where, how much - and on a wide screen, the actions under it -->
<ng-template #summary let-withActions>
  <section class="rounded-xl bg-white p-5 shadow-card">
    <h1 class="m-0 text-2xl font-bold leading-tight text-primary">{{ typeLabel }}</h1>
    <p class="m-0 mt-1 text-sm text-text-secondary">
      <ng-container *ngIf="location">{{ location }} · </ng-container>
      <time [attr.datetime]="accommodation!.created_at">Posted {{ accommodation!._age }}</time>
    </p>
    <p class="m-0 mt-4 text-2xl font-bold leading-tight text-primary">
      <ng-container *ngIf="!isFree && accommodation!._rent; else freeRent">
        &#8358;{{ accommodation!._rent }}<span class="text-base font-medium text-text-secondary">{{ rentIntervalLabel }}</span>
      </ng-container>
      <ng-template #freeRent>{{ isFree ? 'Free' : 'Rent on request' }}</ng-template>
    </p>
    <p *ngIf="accommodation!.roommate_rent" class="m-0 mt-1 text-sm text-text-secondary">
      Your share as a roommate: &#8358;{{ accommodation!.roommate_rent | number }}{{ rentIntervalLabel }}
    </p>
    <div *ngIf="withActions" class="mt-5 flex flex-wrap items-center gap-2">
      <ng-container *ngTemplateOutlet="actions; context: { $implicit: 'panel' }"></ng-container>
    </div>
  </section>
</ng-template>

<!--
  Chat (and Save, Share) for a renter; Manage for the poster; Sign up when logged out. `bar` is the phone's bottom
  bar (Save and Share as icons, Chat taking the rest), `panel` the wide screen's summary.
-->
<ng-template #actions let-place>
  <ng-container *ngIf="!callerService.isLoggedIn">
    <p *ngIf="place === 'bar'" class="m-0 min-w-0 flex-1 truncate text-sm text-text-secondary">Join corpers.ng to chat with {{ posterName }}.</p>
    <a routerLink="/signup" [queryParams]="signupQueryParams"
      class="btn-primary inline-flex min-h-11 items-center justify-center !py-0" [class.w-full]="place === 'panel'">
      Sign up free
    </a>
  </ng-container>

  <ng-container *ngIf="callerService.isLoggedIn && isOwn">
    <p class="m-0 min-w-0 flex-1 text-sm text-text-secondary">
      Your listing · <a routerLink="/dashboard" [queryParams]="{ post: 'accommodation_' + accommodation!.id }" class="font-medium text-primary underline">Manage it on your dashboard</a>
    </p>
  </ng-container>

  <ng-container *ngIf="callerService.isLoggedIn && !isOwn">
    <button type="button" (click)="toggleSave()" [disabled]="isSaving" [attr.aria-pressed]="saved"
      [attr.aria-label]="saved ? 'Saved - remove from saved' : 'Save this house'"
      class="inline-flex min-h-11 flex-shrink-0 items-center justify-center gap-1.5 rounded-lg border border-primary/30 px-3 font-medium text-primary transition hover:bg-primary/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary disabled:opacity-50"
      [class.flex-1]="place === 'panel'">
      <mat-icon class="!size-5 !text-xl !leading-5" aria-hidden="true">{{ saved ? 'bookmark' : 'bookmark_border' }}</mat-icon>
      <span *ngIf="place === 'panel'">{{ saved ? 'Saved' : 'Save' }}</span>
    </button>
    <button type="button" (click)="houseActions.share(accommodation!)" aria-label="Share this house"
      class="inline-flex min-h-11 flex-shrink-0 items-center justify-center gap-1.5 rounded-lg border border-primary/30 px-3 font-medium text-primary transition hover:bg-primary/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
      [class.flex-1]="place === 'panel'">
      <mat-icon class="!size-5 !text-xl !leading-5" aria-hidden="true">share</mat-icon>
      <span *ngIf="place === 'panel'">Share</span>
    </button>
    <button type="button" (click)="houseActions.chat(accommodation!)"
      class="btn-primary inline-flex min-h-11 items-center justify-center gap-2 !py-0"
      [ngClass]="place === 'panel' ? 'order-first w-full' : 'flex-1'">
      <mat-icon class="!size-5 !text-xl !leading-5" aria-hidden="true">chat</mat-icon> Chat {{ posterName }}
    </button>
  </ng-container>
</ng-template>

<!-- The poster's numbers, when they listed some: call from a phone, or copy -->
<ng-template #contact>
  <section *ngIf="accommodation?.contactPhoneNumbers?.length" class="rounded-xl bg-white p-5 shadow-card">
    <h2 class="mb-3 text-lg font-semibold text-primary">Contact</h2>
    <ul class="m-0 list-none space-y-3 p-0">
      <li *ngFor="let phone of accommodation!.contactPhoneNumbers" class="flex items-center justify-between gap-3">
        <div class="min-w-0">
          <p *ngIf="phone.label" class="m-0 text-sm text-text-secondary">{{ phone.label }}</p>
          <p class="m-0 font-medium tabular-nums text-gray-900">{{ phone.number }}</p>
        </div>
        <div class="flex flex-shrink-0 gap-2">
          <a [href]="'tel:' + phone.number" [attr.aria-label]="'Call ' + phone.number"
            class="inline-flex size-10 items-center justify-center rounded-lg border border-primary/30 text-primary hover:bg-primary/5">
            <mat-icon class="!size-5 !text-xl !leading-5" aria-hidden="true">call</mat-icon>
          </a>
          <button type="button" (click)="houseActions.copyPhoneNumber(phone.number)" [attr.aria-label]="'Copy ' + phone.number"
            class="inline-flex size-10 items-center justify-center rounded-lg border border-primary/30 text-primary hover:bg-primary/5">
            <mat-icon class="!size-5 !text-xl !leading-5" aria-hidden="true">content_copy</mat-icon>
          </button>
        </div>
      </li>
    </ul>
  </section>
</ng-template>

<ng-template #poster>
  <section class="rounded-xl bg-white p-5 shadow-card">
    <h2 class="mb-3 text-lg font-semibold text-primary">Posted by</h2>
    <div class="flex items-center gap-3">
      <span class="flex size-11 flex-shrink-0 items-center justify-center rounded-full bg-primary font-bold text-accent" aria-hidden="true">{{ posterInitial }}</span>
      <div class="min-w-0 flex-1">
        <!-- TODO(wachukxs/gh#116): a "Verified corper" label for verified posters (an NYSC ID card upload, not just an email). -->
        <a [routerLink]="posterProfileLink" class="font-medium text-gray-900 hover:text-primary hover:underline">{{ posterName }}</a>
        <p *ngIf="location" class="m-0 text-sm text-text-secondary">{{ location }}</p>
        <p *ngIf="leaving" class="m-0 mt-1 inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-sm font-medium text-primary">
          <mat-icon class="!size-4 !text-base !leading-4" aria-hidden="true">schedule</mat-icon>{{ leaving }}
        </p>
      </div>
      <a [routerLink]="posterProfileLink" class="flex-shrink-0 text-sm font-medium text-primary underline">View profile</a>
    </div>
  </section>
</ng-template>
